import { connect } from 'coframe'
import { smitter } from 'smitter'
import { unikId } from 'unik-id'

import { blendEvents } from '@frame/lib/events'

import {
  BlendEventPayload,
  BlendModalActions,
  BlendModalCallbacks,
  BlendOnCompletePayload,
  BlendScriptConfig,
  Env,
  ErrorCode,
  ExternalEvents,
  FrameOrigin,
  FrameOriginCanary,
  InternalEvents,
  MessageTypes,
  Messages,
  ScriptConfig,
  TrueworkError,
  V2,
} from './types'
import { createFrame, hideFrame, mountFrame, removeFrame } from './utils'

let currentScript: string | undefined

try {
  currentScript = (document.currentScript as HTMLScriptElement)?.src
} catch (e) {
  console.error(e) // eslint-disable-line no-console
}

function init(
  { publishableKey, sessionToken, env, metadata, autoComplete = true, ...config }: ScriptConfig = {} as ScriptConfig
): V2 {
  if (!env) env = Env.Production
  if (!Object.values(Env).includes(env)) throw new TrueworkError(`Invalid environment value: ${env}`)

  let frameOrigin = process.env.TJS_FRAME_ORIGIN || FrameOrigin

  if (currentScript === 'https://js.truework.com/canary') {
    frameOrigin = FrameOriginCanary
  }

  const frame = createFrame({ src: frameOrigin })
  const instanceId = unikId()

  /**
   * The difference between these two is that one is for communication between
   * this shim and the iframe, and the other is a facade that users can
   * integrate against. Both implement smitter's pub/sub interface.
   */
  const internal = connect<InternalEvents>(frame, 'truework-js')
  const external = smitter<ExternalEvents>()

  async function close() {
    // hide the frame immediately
    hideFrame(frame)

    await internal.emit('close')
    // wait for frame to tell us it's shut down
    await new Promise((y) => {
      internal.on('closed', (taskStatuses) => {
        // wait an extra 1 second before actually removing the frame -- if this works well, we can bump this number up
        setTimeout(() => {
          removeFrame(frame)
          y(true)
        }, 5 * 1000)

        external.emit('close', taskStatuses)
      })
    })
  }

  return {
    async open() {
      try {
        const mount = mountFrame(frame)

        // The child frame doesn't have any way of contacting us until we send the first message, so it's okay to set up
        // these listeners after mounting the frame.
        internal.on('error', ({ code, message }) => {
          // up-cycle into Error subclass
          const e = new TrueworkError(message)
          e.code = code
          external.emit('error', e)
          if (e.code === ErrorCode.Critical) removeFrame(frame)
        })
        internal.on('complete', () => {
          external.emit('complete')
        })
        internal.on('close', close)

        // wait for ACK from frame, then notify parent
        internal.on('opened', () => {
          external.emit('open')
        })

        // Instead of waiting for the frame to completely finish loading, we want to instead send 'ping' events up to 10
        // times per second and wait for a 'pong'. When we receive this, we know that the frame has initialized enough
        // that it can
        await new Promise((resolve) => {
          const interval = setInterval(async () => {
            try {
              await internal.emit('ping')
            } catch (e) {
              // message timed out -- this is fine
            }
          }, 100)

          function done() {
            clearInterval(interval)
            resolve(null)
          }

          mount.then(done)
          internal.on('pong', done)
        })

        await internal.emit('open', {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore this is already set above
          env,
          version,
          publishableKey,
          sessionToken,
          instanceId,
          autoComplete,
          metadata,
          ...config,
        })
      } catch (e) {
        external.emit('error', new TrueworkError(`Widget failed to load`))
      }
    },
    close,
    complete: async () => {
      // TODO: Make this work or remove it. The script should never access the API, as it may be blocked by CORS.
      const e = new TrueworkError("Can't complete session")
      e.code = ErrorCode.Critical
      external.emit('error', e)
      return
    },
    on(type, handler) {
      return external.on(type, handler)
    },
    onOpen(handler) {
      return external.on('open', handler)
    },
    onClose(handler) {
      return external.on('close', handler)
    },
    /**
     * @deprecated Please use onComplete instead
     */
    onSuccess(handler) {
      return external.on('complete', handler)
    },
    onComplete(handler) {
      return external.on('complete', handler)
    },
    onError(handler) {
      return external.on('error', handler)
    },
  }
}

function initBlend(config: BlendScriptConfig, callbacks: BlendModalCallbacks): BlendModalActions {
  const direct = init(config)

  return {
    async open() {
      await direct.open()

      blendEvents.on('event', (data: BlendEventPayload) => {
        callbacks.onEvent(data)
      })

      blendEvents.on('complete', (data: BlendOnCompletePayload) => {
        callbacks.onComplete(data)
      })
    },
    async close() {
      await direct.close()
    },
  }
}

export const version = process.env.VERSION || 'unknown'

export type { Messages }

export { Env, ErrorCode, MessageTypes }

export const credentials = {
  Env,
  ErrorCode,
  init,
  MessageTypes,
}

export const direct = {
  init,
  blend: {
    init: initBlend,
  },
  Env,
  ErrorCode,
  MessageTypes,
}
